import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, exhaustMap, last, map, mergeMap, of, switchMap } from 'rxjs';

import { HandbookApiService } from '@core/data/api/handbook-api.service';
import {
  handbookAttachmentFromDto,
  handbookSectionFromDto,
  handbookStatusToDto,
} from '@core/data/api/handbook.mapper';
import { UpdateHandbookSectionDto } from '@core/data/api/handbook.dto';

import { HandbookApiActions, HandbookPageActions } from './handbook.actions';

/**
 * Bridges handbook actions to `/api/v1/handbook`. The effects own the async work
 * and its error handling so the reducer stays pure.
 */
@Injectable()
export class HandbookEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(HandbookApiService);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandbookPageActions.entered),
      switchMap(() =>
        this.api.listSections().pipe(
          map((rows) =>
            HandbookApiActions.loadSuccess({ sections: rows.map(handbookSectionFromDto) }),
          ),
          catchError((err: unknown) =>
            of(HandbookApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * A section's files, read when its panel opens rather than with the list.
   *
   * `mergeMap`, not `switchMap`: opening a second section must not cancel the
   * first one's read, because both panels stay on screen.
   */
  readonly loadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandbookPageActions.sectionOpened),
      mergeMap(({ sectionGuid }) => this.attachments$(sectionGuid)),
    ),
  );

  /**
   * Saves an edit. **Only what changed is sent** — this is one of the few true
   * patches in the system, so an absent key is left alone rather than cleared,
   * and sending the whole form would stamp fields nobody touched.
   */
  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandbookPageActions.sectionSaved),
      // `exhaustMap`: a double-clicked Save is one save, not two.
      exhaustMap(({ sectionGuid, changes }) => {
        const body: UpdateHandbookSectionDto = {};
        if (changes.title !== undefined) body.title = changes.title;
        if (changes.body !== undefined) body.body = changes.body;
        if (changes.author !== undefined) body.authorName = changes.author;
        if (changes.status !== undefined) body.publicationStatus = handbookStatusToDto(changes.status);
        return this.api.updateSection(sectionGuid, body).pipe(
          map((dto) => HandbookApiActions.sectionUpdated({ section: handbookSectionFromDto(dto) })),
          catchError((err: unknown) =>
            of(HandbookApiActions.writeFailure({ error: toMessage(err) })),
          ),
        );
      }),
    ),
  );

  /**
   * Uploads the picked files, then re-reads the section's list.
   *
   * One POST per file, which is how the server takes them, run in sequence so a
   * failure stops rather than racing. The re-read is because each upload answers
   * only its own file and the list is what the panel renders.
   */
  readonly attach$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandbookPageActions.filesAttached),
      mergeMap(({ sectionGuid, files }) =>
        concat(...files.map((f) => this.api.attach(sectionGuid, f))).pipe(
          last(),
          switchMap(() => this.attachments$(sectionGuid)),
          catchError((err: unknown) =>
            of(HandbookApiActions.writeFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /** Removes a file **and the bytes behind it**, then re-reads the list. */
  readonly removeAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HandbookPageActions.attachmentRemoved),
      mergeMap(({ sectionGuid, fileGuid }) =>
        this.api.removeAttachment(fileGuid).pipe(
          switchMap(() => this.attachments$(sectionGuid)),
          catchError((err: unknown) =>
            of(HandbookApiActions.writeFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /** One section's attachment list, as an action. The three writes above all end here. */
  private attachments$(sectionGuid: string) {
    return this.api.listAttachments(sectionGuid).pipe(
      map((rows) =>
        HandbookApiActions.attachmentsLoaded({
          sectionGuid,
          attachments: rows.map(handbookAttachmentFromDto),
        }),
      ),
      catchError((err: unknown) => of(HandbookApiActions.writeFailure({ error: toMessage(err) }))),
    );
  }
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
